/**
 * @file hooks/useResponsive.ts
 * @description 統一的響應式斷點 Hook（薄包裝 antd Grid.useBreakpoint）。
 *
 * 設計目的：
 * - 全項目共用一個語義化的斷點判斷，避免各頁面再各自調 useBreakpoint。
 * - 對齊 ZHCB design tokens（與 styles/tokens.ts 的 breakpoint 一致）。
 *
 * iPad 場景對應：
 * - iPad mini / 普通 iPad 直立 (768×1024)：md && !lg → isPadPortrait
 * - iPad 橫向 (1024×768) / iPad Pro (1024~1366)：lg && !xl → isPadLandscape
 * - iPhone / 小螢幕 (<768)：!md → isMobile
 *
 * 使用範例：
 * ```tsx
 * const { isMobile, isPadPortrait, isPadLandscape } = useResponsive();
 * if (isPadPortrait) return <CompactLayout />;
 * ```
 */
import { Grid } from 'antd';

const { useBreakpoint } = Grid;

/** antd Grid 的 screens 鍵（true 表示「達到該斷點」） */
export interface ResponsiveScreens {
  xs: boolean;
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
  xxl: boolean;
}

/** useResponsive 返回的語義判斷結果 */
export interface ResponsiveState {
  /** 原始 antd screens（部分頁面需要直接判斷某斷點是否到達） */
  screens: ResponsiveScreens;
  /** < md (< 768px)：手機螢幕 */
  isMobile: boolean;
  /** 768 ≤ width < 1200：iPad 範圍（包含直立與部分橫向） */
  isPad: boolean;
  /** 768 ≤ width < 992：iPad 直立（md && !lg） */
  isPadPortrait: boolean;
  /** 992 ≤ width < 1200：iPad 橫向 / 小桌面（lg && !xl） */
  isPadLandscape: boolean;
  /** ≥ 1200：標準桌面 */
  isDesktop: boolean;
}

/**
 * 響應式斷點 Hook。
 *
 * 注意：antd Grid.useBreakpoint() 在 SSR 時可能返回空 object，
 * 此處兜底為 false，確保桌面/移動的初始判斷不會 crash。
 */
export function useResponsive(): ResponsiveState {
  const screens = useBreakpoint();

  // SSR 兜底（雖然 SPA 場景理論上不會發生，仍保留）
  /**
   * antd Grid.useBreakpoint() 在首幀（內部 layout effect 訂閱 responsiveObserver
   * 之前）返回空對象 {}。若此時用 `!!screens.md` 兜底，safe 全為 false →
   * isMobile = !md = true，桌面會被誤判成手機：
   *   - BaseLayout 首幀渲染 Drawer 形態、mainMarginLeft = 0；
   *   - 下一幀 useBreakpoint 纠正成桌面後 mainMarginLeft = sider.width(240)；
   *   - 配合主內容區 `transition: margin-left 0.2s`，播成「硬刷新後整體左右
   *     橫移一下」（且因 cssinjs 首屏注入樣式觸發 reflow，0→240 會被當成可過渡
   *     的數值變化而真實播放動畫）。
   * 解法：首幀改用 window.innerWidth 同步推算斷點（與 antd 預設斷點對齊），
   * 確保「第一次渲染」就拿到正確的桌面/手機判斷，從源頭消除佈局閃移。
   */
  const isFirstFrame = Object.keys(screens).length === 0;
  const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const safe: ResponsiveScreens = isFirstFrame
    ? {
        xs: vw < 576,
        sm: vw >= 576,
        md: vw >= 768,
        lg: vw >= 992,
        xl: vw >= 1200,
        xxl: vw >= 1600,
      }
    : {
        xs: !!screens.xs,
        sm: !!screens.sm,
        md: !!screens.md,
        lg: !!screens.lg,
        xl: !!screens.xl,
        xxl: !!screens.xxl,
      };

  const isMobile = !safe.md;
  const isPadPortrait = safe.md && !safe.lg;
  const isPadLandscape = safe.lg && !safe.xl;
  const isPad = isPadPortrait || isPadLandscape;
  const isDesktop = safe.xl;

  return {
    screens: safe,
    isMobile,
    isPad,
    isPadPortrait,
    isPadLandscape,
    isDesktop,
  };
}
